import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Box, Button, ButtonSize, PagePanel } from 'edenred-ui';
import { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import config from '@epi-config';
import { ICardOrderSuccessfulScreen } from '@epi-models/containers/OrderStatus';
import { useMenuDimensions, useOnboardingWizard } from '@epi-shared/hooks';
import { DownloadInvoiceButton } from '@epi-components/DownloadInvoiceButton/DownloadInvoiceButton';
import { getUserCompany } from '@epi-actions/api';
import { getCustomerNumber } from '@epi-selectors/api';

import { PaymentMethods } from '../../constants/paymentMethods';
import { routerPaths } from '../../constants/routerPaths';

const { enableDepositsUpdate, depositUpdateAllowedIds } = config;

export const CardOrderSuccessfulScreen = ({
  isPaymentSuccess,
  getTranslationPath,
  paymentMethod,
  goToPage,
  paymentId,
  isReceiptOnly,
  isDeposit,
  footer
}: ICardOrderSuccessfulScreen) => {
  const { t } = useTranslation();
  const { isMobile } = useMenuDimensions();
  const { fontSize } = useTheme();
  const dispatch = useDispatch();
  const companyId = useSelector(getCustomerNumber);
  const allowDepositUpdate =
    enableDepositsUpdate || depositUpdateAllowedIds.includes(companyId);
  const { isOnBoardingActive } = useOnboardingWizard();
  const redirectToReports = () => {
    goToPage(
      `${
        isDeposit && allowDepositUpdate
          ? routerPaths.reportsDeposits
          : routerPaths.reportsTransactions
      }`
    );
  };

  const getTitle = () =>
    t(
      getTranslationPath(
        paymentMethod === PaymentMethods.EInvoice ||
          paymentMethod === PaymentMethods.PdfInvoice
          ? `title_success_${paymentMethod}`
          : 'title_success',
        isPaymentSuccess
      )
    );

  const getBody = () =>
    t(
      getTranslationPath(
        paymentMethod === PaymentMethods.EInvoice ||
          paymentMethod === PaymentMethods.PdfInvoice
          ? `body_success_${paymentMethod}`
          : 'body_success',
        isPaymentSuccess
      )
    );

  useEffect(() => {
    dispatch(getUserCompany());
  }, []);

  return (
    <PagePanel footer={footer} title={getTitle()}>
      <Box fontSize={fontSize} py={2}>
        {getBody()}
      </Box>
      {paymentMethod === PaymentMethods.PrintInvoicePdf && (
        <h2 className="small">
          {t('containers.order_status.body_reference_number_info')}
        </h2>
      )}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={3}
        mt={3}
      >
        {!allowDepositUpdate && (
          <div>
            <DownloadInvoiceButton
              id={paymentId ?? 'download-invoice-button'}
              idType="payment"
              className="btn-download"
            >
              <span>
                {isReceiptOnly
                  ? t('containers.order_status.download_receipt')
                  : t('containers.order_status.download_invoice')}
              </span>
            </DownloadInvoiceButton>
          </div>
        )}
        {!isOnBoardingActive && (
          <Button
            id="go-to-transactions-history-btn"
            size={ButtonSize.Medium}
            onClick={redirectToReports}
          >
            {t(getTranslationPath('button_success'))}
          </Button>
        )}
      </Box>
    </PagePanel>
  );
};
